import { signIn } from "@/lib/cloudflare/auth-client";
import { buildGettingStartedPath, startTrialCheckout } from "@/lib/cloudflare/billing-client";
import { IconArrowRight, IconCheck, IconShieldCheck, IconSparkles, IconLayoutKanban, IconTrash, IconStack2 } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";
import { usePostHog } from "posthog-js/react";
import { isActiveSubscription, type Subscription } from "@/types/subscription";

interface PricingTableProps {
  subscription?: Subscription | null;
  isPublicPage?: boolean;
}

const workflowChanges = [
  {
    title: "Visual Kanban sorting",
    description: "Stop clicking through emails individually. Drag and drop to sort, correct the AI, and batch execute everything in one click.",
    icon: IconLayoutKanban,
  },
  {
    title: "One-click mass unsubscribe",
    description: "We find every newsletter and marketing list hiding in your inbox. Purge them all instantly from the Unsubscribe Queue.",
    icon: IconTrash,
  },
  {
    title: "Plain-English automations",
    description: "Just type 'Archive emails with PDF attachments'. Our AI builds robust local rules that run automatically every morning.",
    icon: IconSparkles,
  },
  {
    title: "Custom workflow columns",
    description: "Build your perfect workflow. Create custom columns based on specific senders, AI labels, or projects.",
    icon: IconStack2,
  },
];

const proIncludes = [
  "Unlimited AI email triage",
  "Visual Kanban processing board",
  "One-click mass unsubscribe",
  "Bulk archive and delete",
  "Plain-English automations",
  "Custom workflow columns",
  "Explainable AI decision logs",
  "Real-time background sync",
  "First-class dark mode support",
  "Secure OAuth integration",
  "Minimal metadata footprint"
];

export function PricingTable({ subscription, isPublicPage = false }: PricingTableProps) {
  const posthog = usePostHog();
  const [isAnnual, setIsAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const isCurrentPlan = (planName: string) => {
    return isActiveSubscription(subscription) &&
      subscription?.plan?.toLowerCase() === planName.toLowerCase();
  };

  const trialEndsLabel = subscription?.trialEndsAt
    ? new Date(subscription.trialEndsAt * 1000).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const handleUpgrade = async (plan: string) => {
    try {
      setLoadingPlan(plan);
      posthog.capture('clicked_upgrade_plan', { plan, interval: isAnnual ? 'yearly' : 'monthly', source: 'billing_page' });
      const checkoutUrl = await startTrialCheckout({
        plan: plan.toLowerCase() as 'pro' | 'business',
        interval: isAnnual ? 'yearly' : 'monthly',
      });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to process upgrade");
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleAction = async (planName: string) => {
    if (isPublicPage) {
      posthog.capture('clicked_upgrade_plan', { plan: planName, interval: isAnnual ? 'yearly' : 'monthly', source: 'public_pricing_page' });
      signIn(
        buildGettingStartedPath({
          plan: planName.toLowerCase() as 'pro' | 'business',
          interval: isAnnual ? 'yearly' : 'monthly',
        })
      );
    } else {
      await handleUpgrade(planName);
    }
  };

  const monthlyPrice = 19;
  const yearlyPrice = 9;
  const currentPrice = isAnnual ? yearlyPrice : monthlyPrice;
  const isPro = isCurrentPlan('pro');
  const actionLabel = isPublicPage ? 'Connect Gmail to continue' : 'Start 30-day free trial';

  return (
    <div className="w-full">
      {!isPro ? (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Pricing Card */}
          <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200 lg:col-span-1 flex flex-col relative overflow-hidden">
            <div className="relative z-10 flex flex-col flex-1">
              <h3 className="text-xl font-semibold text-slate-950">Pro Plan</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Win the inbox hour with full automation and unlimited email processing.
              </p>
              
              <div className="mt-6 flex items-center gap-1 rounded-full bg-slate-100 p-1 self-start">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
                    !isAnnual ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
                    isAnnual ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  Yearly
                </button>
              </div>

              <div className="mt-8 flex items-baseline gap-2">
                <span className="text-5xl font-semibold tracking-tight text-slate-950">${currentPrice}</span>
                <span className="text-sm font-medium text-slate-500">/mo</span>
              </div>
              <p className="mt-2 text-sm font-medium text-blue-600">
                {isAnnual
                  ? '30-day free trial. Then $108/year. Cancel before billing starts.'
                  : '30-day free trial. Then $19/month. Cancel before billing starts.'}
              </p>

              <div className="mt-auto pt-8">
                <button
                  onClick={() => handleAction('pro')}
                  disabled={loadingPlan === 'pro'}
                  className="w-full inline-flex h-14 items-center justify-center rounded-full bg-slate-950 px-6 text-sm font-semibold text-white transition-all hover:bg-slate-800 disabled:opacity-50 hover:-translate-y-0.5 shadow-xl shadow-slate-900/10"
                >
                  {loadingPlan === 'pro' ? 'Loading...' : actionLabel}
                  {!loadingPlan && <IconArrowRight className="ml-2 h-4 w-4" />}
                </button>
                <p className="mt-4 text-center text-xs font-medium text-slate-500">
                  Card required. No charge until your 30-day trial ends.
                </p>
              </div>
            </div>
            
            {/* Decorative background element */}
            <div className="absolute right-[-40px] top-[-40px] h-40 w-40 rounded-full bg-blue-50/50 blur-3xl pointer-events-none" />
          </div>

          {/* Features Bento Grid */}
          <div className="lg:col-span-2 grid gap-6 sm:grid-cols-2">
            {workflowChanges.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200 flex flex-col">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 mb-6">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-950">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-3xl bg-green-50 p-8 shadow-sm ring-1 ring-green-200 flex flex-col items-center text-center max-w-xl mx-auto">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600 mb-6">
            <IconCheck className="h-8 w-8" />
          </div>
          <h3 className="text-2xl font-semibold text-green-950">
            {subscription?.status === 'trialing' ? 'Your Pro trial is active' : "You're on Pro"}
          </h3>
          <p className="mt-2 text-green-800">
            {subscription?.status === 'trialing' && trialEndsLabel
              ? `All features are unlocked. You won't be billed until ${trialEndsLabel}.`
              : 'All features unlocked. Enjoy your quiet inbox.'}
          </p>
        </div>
      )}

      {/* Everything in Pro List */}
      {!isPro && (
        <div className="mt-6 rounded-3xl bg-slate-950 p-8 md:p-10 shadow-sm ring-1 ring-slate-900 relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-xl font-semibold text-white mb-8">Everything you need to reach Inbox Zero</h3>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {proIncludes.map((item) => (
                <div key={item} className="flex items-start gap-3 text-sm text-slate-300">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-white mt-0.5">
                    <IconCheck className="h-3.5 w-3.5" />
                  </div>
                  <span className="leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Decorative background element */}
          <div className="absolute right-0 bottom-0 opacity-[0.03] pointer-events-none transform translate-x-1/4 translate-y-1/4">
            <svg className="w-64 h-64 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}
